import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Checkbox,
  Divider,
  Flex,
  Input,
  SimpleGrid,
  Text,
  VStack
} from '@chakra-ui/react'
import { AxiosError } from 'axios'
import { useEffect, useMemo, useRef, useState } from 'react'

import { ActionButton } from '../../../../components/ActionButton'
import { ConfirmDialog } from '../../../../components/ConfirmDialog'
import { DetailField } from '../../../../components/DetailField'
import { EmptyState } from '../../../../components/EmptyState'
import { FileInput } from '../../../../components/FileInput'
import { FormField } from '../../../../components/FormField'
import { FormModal } from '../../../../components/FormModal'
import { Icon } from '../../../../components/Icon'
import { LoadingBlock } from '../../../../components/Loading'
import { MaskedInput } from '../../../../components/MaskedInput'
import { SectionTitle } from '../../../../components/SectionCard'
import { FormSelect, SearchableSelect } from '../../../../components/Select'
import { formatCurrency, getApiErrorMessage, toFormErrors, useAppToast } from '../../../../helpers'
import { EventService } from '../../../../services/Event/EventService'
import { CheckoutInfo, CheckoutWonArt, PanelCheckoutPaymentMethod } from '../../../../services/Event/types'
import {
  computeBill,
  flattenCatalog,
  ICheckoutAdjustments,
  ICheckoutCatalogProduct,
  NO_ADJUSTMENTS
} from './checkoutBill'

// PanelCheckoutPaymentRequest: `proof_image => file|mimes:jpeg,jpg,png,pdf|max:5120`.
const PROOF_MAX_MB = 5

const METHOD_OPTIONS: { value: PanelCheckoutPaymentMethod; label: string }[] = [
  { value: 'pix_manual', label: 'PIX (transferência)' },
  { value: 'cash', label: 'Dinheiro' },
  { value: 'credit_manual', label: 'Crédito (outra máquina)' },
  { value: 'debit_manual', label: 'Débito (outra máquina)' }
]

const ITEM_BOX = { p: 3, borderRadius: 'lg', border: '1px', borderColor: 'gray.100', bg: 'gray.50' } as const

const EMPTY_ERRORS = {
  'buyer.name': '',
  'buyer.cpf': '',
  'buyer.phone': '',
  payment_method: '',
  payment_method_note: '',
  proof_image: ''
}

interface IBuyer {
  name: string
  cpf: string
  phone: string
}

export interface ICheckoutPaymentModalProps {
  isOpen: boolean
  onClose: () => void
  eventId: number
  attendee: { id: number; name: string } | null
  /** After the check-out: the page reloads the attendees. */
  onDone: () => void
}

/**
 * Check-out with payment (ABRA-425): an attendee who still owes on the tab leaves with the payment
 * the admin registers, as in the terminal's manual payment. The bill on the left, the payment on
 * the right.
 */
export default function CheckoutPaymentModal({
  isOpen,
  onClose,
  eventId,
  attendee,
  onDone
}: ICheckoutPaymentModalProps) {
  const toast = useAppToast()
  const eventService = new EventService()

  const [info, setInfo] = useState<CheckoutInfo | null>(null)
  const [loadError, setLoadError] = useState(false)
  /** The API refused to read the tab (open auction, no active check-in): nothing to charge. */
  const [blockedMessage, setBlockedMessage] = useState<string | null>(null)
  const [adjustments, setAdjustments] = useState<ICheckoutAdjustments>(NO_ADJUSTMENTS)
  const [products, setProducts] = useState<ICheckoutCatalogProduct[] | null>(null)
  const [productsLoading, setProductsLoading] = useState(false)
  const [isAddingProduct, setIsAddingProduct] = useState(false)
  const [buyer, setBuyer] = useState<IBuyer>({ name: '', cpf: '', phone: '' })
  const [method, setMethod] = useState<PanelCheckoutPaymentMethod | ''>('')
  const [note, setNote] = useState('')
  const [proof, setProof] = useState<File | null>(null)
  const [errors, setErrors] = useState(EMPTY_ERRORS)
  const [submitting, setSubmitting] = useState(false)
  const [artToRemove, setArtToRemove] = useState<CheckoutWonArt | null>(null)

  // A tab opened right after another must not take the late response of the first.
  const requestIdRef = useRef(0)

  useEffect(() => {
    if (!isOpen || !attendee) return
    setInfo(null)
    setAdjustments(NO_ADJUSTMENTS)
    setIsAddingProduct(false)
    setMethod('')
    setNote('')
    setProof(null)
    setErrors(EMPTY_ERRORS)
    loadInfo()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, attendee?.id])

  function loadInfo() {
    if (!attendee) return
    const reqId = ++requestIdRef.current
    setLoadError(false)
    setBlockedMessage(null)
    eventService
      .getCheckoutInfo(eventId, attendee.id)
      .then((data) => {
        if (reqId !== requestIdRef.current) return
        setInfo(data)
        setBuyer({
          name: data.participant?.name ?? attendee.name,
          cpf: data.participant?.cpf ?? '',
          phone: data.participant?.phone ?? ''
        })
      })
      .catch((error) => {
        if (reqId !== requestIdRef.current) return
        if ((error as AxiosError)?.response?.status === 422) {
          setBlockedMessage(getApiErrorMessage(error) ?? 'Esta comanda não pode ser fechada agora.')
          return
        }
        setLoadError(true)
        toast.error('Erro ao carregar a comanda', error)
      })
  }

  function openProductPicker() {
    setIsAddingProduct(true)
    if (products !== null || productsLoading) return
    setProductsLoading(true)
    eventService
      .getCheckoutCatalog(eventId)
      .then((catalog) => setProducts(flattenCatalog(catalog)))
      .catch((error) => {
        setIsAddingProduct(false)
        toast.error('Erro ao carregar os produtos', error)
      })
      .finally(() => setProductsLoading(false))
  }

  const bill = useMemo(
    () => (info ? computeBill(info, adjustments, products ?? []) : null),
    [info, adjustments, products]
  )

  const hasArtworks = (bill?.keptArts.length ?? 0) > 0
  const hasCharge = (bill?.total ?? 0) > 0
  const proofRequired = hasCharge && method !== '' && method !== 'cash'
  const buyerComplete = buyer.cpf.replace(/\D/g, '').length === 11 && buyer.phone.replace(/\D/g, '').length >= 10

  const isConfirmDisabled =
    !info ||
    !bill ||
    submitting ||
    (hasArtworks && !buyerComplete) ||
    (hasCharge && (method === '' || (proofRequired && !proof)))

  const toggle = <T,>(list: T[], value: T) =>
    list.includes(value) ? list.filter((item) => item !== value) : [...list, value]

  const setUnits = (key: 'removedUnits' | 'addedProducts', id: number, units: number) =>
    setAdjustments((prev) => {
      const next = { ...prev[key] }
      if (units > 0) next[id] = units
      else delete next[id]
      return { ...prev, [key]: next }
    })

  const handleSubmit = async () => {
    if (!attendee || !info || !bill || isConfirmDisabled) return

    const formData = new FormData()
    formData.append('participant_id', String(attendee.id))
    if (hasCharge) {
      formData.append('payment_method', method)
      formData.append('amount', bill.total.toFixed(2))
      if (note.trim()) formData.append('payment_method_note', note.trim())
      if (proof) formData.append('proof_image', proof)
    }

    // An empty field is left out, so the registration keeps its value.
    const buyerData = Object.fromEntries(Object.entries(buyer).filter(([, value]) => value.trim() !== ''))
    if (Object.keys(buyerData).length > 0) formData.append('buyer', JSON.stringify(buyerData))

    const lists: Record<string, unknown[]> = {
      frame_art_ids: adjustments.frameArtIds.filter((id) => !adjustments.removedArtIds.includes(id)),
      establishment_order_ids: info.establishment_orders.map((order) => order.id),
      removed_art_ids: adjustments.removedArtIds,
      removed_establishment_items: Object.entries(adjustments.removedUnits).map(([id, quantity]) => ({
        id: Number(id),
        quantity
      })),
      added_products: Object.entries(adjustments.addedProducts).map(([productId, quantity]) => ({
        product_id: Number(productId),
        quantity
      }))
    }
    Object.entries(lists).forEach(([key, list]) => {
      if (list.length > 0) formData.append(key, JSON.stringify(list))
    })

    setSubmitting(true)
    setErrors(EMPTY_ERRORS)
    try {
      await eventService.checkoutWithPayment(eventId, formData)
      toast.success('Check-out realizado', attendee.name)
      onDone()
      onClose()
    } catch (error) {
      const response = (error as AxiosError<{ errors?: Record<string, string[]> }>)?.response
      if (response?.status === 400) setErrors(toFormErrors(EMPTY_ERRORS, response.data?.errors))
      toast.error('Erro ao registrar o check-out', error)
    } finally {
      setSubmitting(false)
    }
  }

  const productOptions = (products ?? []).map((product) => ({
    value: product.id,
    label: `${product.name} · ${product.establishmentName} — ${formatCurrency(product.price)}`
  }))

  const removedItemLines = info
    ? info.establishment_orders.flatMap((order) =>
        order.items
          .filter((item) => item.id !== null && (adjustments.removedUnits[item.id] ?? 0) > 0)
          .map((item) => ({ item, units: adjustments.removedUnits[item.id as number] }))
      )
    : []

  return (
    <>
      <FormModal
        isOpen={isOpen}
        onClose={onClose}
        title="Check-out com pagamento"
        size="wide"
        onSubmit={handleSubmit}
        confirmLabel={hasCharge || !bill ? 'Registrar e fechar comanda' : 'Fechar comanda'}
        isLoading={submitting}
        isConfirmDisabled={isConfirmDisabled}
        isDismissible={!submitting}
      >
        {loadError ? (
          <EmptyState
            icon="lni lni-list"
            tone="error"
            title="Não foi possível carregar a comanda"
            action={<ActionButton action="refresh" label="Tentar novamente" showLabel mr={0} onClick={loadInfo} />}
          />
        ) : blockedMessage ? (
          <Alert status="warning" borderRadius="md">
            <AlertIcon />
            <AlertDescription>{blockedMessage}</AlertDescription>
          </Alert>
        ) : !info || !bill ? (
          <LoadingBlock entity="comanda" />
        ) : (
          <>
            <Text variant="caption" mb={4}>
              {info.participant?.name ?? attendee?.name} · Cartão {info.tab_code}
            </Text>
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
              <Box minW={0}>
                <SectionTitle as="h3">Comanda</SectionTitle>
                <VStack align="stretch" spacing={4}>
                  {info.won_arts.length > 0 && (
                    <Box>
                      <SectionTitle variant="mini" as="h4">
                        Obras do leilão
                      </SectionTitle>
                      <VStack align="stretch" spacing={2}>
                        {bill.keptArts.map((art) => (
                          <Box key={art.id} {...ITEM_BOX}>
                            <Flex justify="space-between" align="flex-start" gap={3}>
                              <Box minW={0}>
                                <Text fontWeight="700" color="gray.800">
                                  {art.title}
                                </Text>
                                {(art.system_fee ?? 0) > 0 && (
                                  <Text variant="caption">
                                    Lance {formatCurrency(art.bid)} + taxa sistêmica{' '}
                                    {formatCurrency(art.system_fee ?? 0)}
                                  </Text>
                                )}
                              </Box>
                              <Flex align="center" flexShrink={0}>
                                <Text fontWeight="700" color="gray.800" mr={2}>
                                  {formatCurrency(art.current_bid)}
                                </Text>
                                <ActionButton
                                  action="delete"
                                  label="Tirar da cobrança"
                                  size="xs"
                                  mr={0}
                                  onClick={() => setArtToRemove(art)}
                                />
                              </Flex>
                            </Flex>
                            {(art.frame_value ?? 0) > 0 && (
                              <Checkbox
                                mt={2}
                                isChecked={adjustments.frameArtIds.includes(art.id)}
                                onChange={() =>
                                  setAdjustments((prev) => ({ ...prev, frameArtIds: toggle(prev.frameArtIds, art.id) }))
                                }
                              >
                                <Text fontSize="sm">Incluir moldura + {formatCurrency(art.frame_value ?? 0)}</Text>
                              </Checkbox>
                            )}
                          </Box>
                        ))}
                        {bill.keptArts.length === 0 && <Text variant="caption">Todas as obras foram tiradas</Text>}
                      </VStack>
                    </Box>
                  )}

                  {info.establishment_orders.length > 0 && (
                    <Box>
                      <SectionTitle variant="mini" as="h4">
                        Compras em estabelecimentos
                      </SectionTitle>
                      <VStack align="stretch" spacing={2}>
                        {info.establishment_orders.map((order) => (
                          <Box key={order.id} {...ITEM_BOX}>
                            <Text fontWeight="700" color="gray.800" mb={1}>
                              #{order.number} · {order.establishment_name}
                            </Text>
                            {order.items.map((item, index) => {
                              const left =
                                item.quantity - (item.id === null ? 0 : (adjustments.removedUnits[item.id] ?? 0))
                              return (
                                <Flex
                                  key={item.id ?? `workshop-${index}`}
                                  justify="space-between"
                                  align="center"
                                  gap={3}
                                >
                                  <Box minW={0}>
                                    <Text fontSize="sm" color={left === 0 ? 'gray.400' : 'gray.700'}>
                                      {left}× {item.name}
                                    </Text>
                                    {item.attendees.length > 0 && (
                                      <Text variant="caption">Inscritos: {item.attendees.join(', ')}</Text>
                                    )}
                                  </Box>
                                  <Flex align="center" flexShrink={0}>
                                    <Text fontSize="sm" mr={2}>
                                      {formatCurrency(item.unit_price * left)}
                                    </Text>
                                    {/* A workshop seat has a name: it is not taken off at checkout (ABRA-399). */}
                                    {item.id !== null && (
                                      <ActionButton
                                        action="delete"
                                        label="Tirar 1 da cobrança"
                                        size="xs"
                                        mr={0}
                                        isDisabled={left === 0}
                                        onClick={() =>
                                          setUnits('removedUnits', item.id as number, item.quantity - left + 1)
                                        }
                                      />
                                    )}
                                  </Flex>
                                </Flex>
                              )
                            })}
                          </Box>
                        ))}
                      </VStack>
                    </Box>
                  )}

                  {info.can_add_products && (
                    <Box>
                      <SectionTitle
                        variant="mini"
                        as="h4"
                        actions={
                          isAddingProduct ? (
                            // Not Escape: in the search it closes the whole modal.
                            <ActionButton
                              action="cancel"
                              showLabel
                              size="xs"
                              mr={0}
                              onClick={() => setIsAddingProduct(false)}
                            />
                          ) : (
                            <ActionButton
                              action="create"
                              label="Adicionar item"
                              showLabel
                              size="xs"
                              mr={0}
                              onClick={openProductPicker}
                            />
                          )
                        }
                      >
                        Adicionados no checkout
                      </SectionTitle>
                      <VStack align="stretch" spacing={2}>
                        {isAddingProduct && (
                          // The field is locked while the catalog loads, and a locked field takes no
                          // autofocus: it mounts again once the products arrive.
                          <SearchableSelect<number>
                            key={products === null ? 'loading' : 'ready'}
                            options={productOptions}
                            value=""
                            onChange={(productId) => {
                              if (productId === '') return
                              setUnits('addedProducts', productId, (adjustments.addedProducts[productId] ?? 0) + 1)
                              setIsAddingProduct(false)
                            }}
                            isLoading={productsLoading}
                            placeholder="Buscar produto"
                            entity="produto"
                            autoFocus
                          />
                        )}
                        {Object.entries(adjustments.addedProducts).map(([productId, quantity]) => {
                          const product = products?.find((p) => p.id === Number(productId))
                          if (!product) return null
                          return (
                            <Flex key={productId} {...ITEM_BOX} justify="space-between" align="center" gap={3}>
                              <Box minW={0}>
                                <Text fontSize="sm" color="gray.700">
                                  {quantity}× {product.name}
                                </Text>
                                <Text variant="caption">{product.establishmentName}</Text>
                              </Box>
                              <Flex align="center" flexShrink={0}>
                                <Text fontSize="sm" mr={2}>
                                  {formatCurrency(product.price * quantity)}
                                </Text>
                                <ActionButton
                                  action="delete"
                                  label="Tirar 1"
                                  size="xs"
                                  onClick={() => setUnits('addedProducts', product.id, quantity - 1)}
                                />
                                <ActionButton
                                  action="create"
                                  label="Adicionar mais 1"
                                  size="xs"
                                  mr={0}
                                  isDisabled={quantity >= 99}
                                  onClick={() => setUnits('addedProducts', product.id, quantity + 1)}
                                />
                              </Flex>
                            </Flex>
                          )
                        })}
                        {!isAddingProduct && Object.keys(adjustments.addedProducts).length === 0 && (
                          <Text variant="caption">Nenhum item adicionado</Text>
                        )}
                      </VStack>
                    </Box>
                  )}

                  {(bill.removedArts.length > 0 || removedItemLines.length > 0) && (
                    <Box>
                      <SectionTitle variant="mini" as="h4">
                        Removidos (não serão cobrados)
                      </SectionTitle>
                      <VStack align="stretch" spacing={2}>
                        {bill.removedArts.map((art) => (
                          <Flex key={`art-${art.id}`} justify="space-between" align="center" gap={3}>
                            <Text fontSize="sm" color="gray.500" textDecoration="line-through" minW={0}>
                              {art.title} · {formatCurrency(art.current_bid)}
                            </Text>
                            <ActionButton
                              action="cancel"
                              icon={<Icon name="lni lni-reply" />}
                              label="Desfazer"
                              showLabel
                              size="xs"
                              mr={0}
                              onClick={() =>
                                setAdjustments((prev) => ({
                                  ...prev,
                                  removedArtIds: prev.removedArtIds.filter((id) => id !== art.id)
                                }))
                              }
                            />
                          </Flex>
                        ))}
                        {removedItemLines.map(({ item, units }) => (
                          <Flex key={`item-${item.id}`} justify="space-between" align="center" gap={3}>
                            <Text fontSize="sm" color="gray.500" textDecoration="line-through" minW={0}>
                              {units}× {item.name} · {formatCurrency(item.unit_price * units)}
                            </Text>
                            <ActionButton
                              action="cancel"
                              icon={<Icon name="lni lni-reply" />}
                              label="Desfazer"
                              showLabel
                              size="xs"
                              mr={0}
                              onClick={() => setUnits('removedUnits', item.id as number, 0)}
                            />
                          </Flex>
                        ))}
                      </VStack>
                    </Box>
                  )}

                  <Box>
                    <Divider mb={3} />
                    <VStack align="stretch" spacing={1}>
                      {bill.bids > 0 && <DetailField layout="inline" label="Obras" value={formatCurrency(bill.bids)} />}
                      {bill.systemFee > 0 && (
                        <DetailField layout="inline" label="Taxa sistêmica" value={formatCurrency(bill.systemFee)} />
                      )}
                      {bill.frames > 0 && (
                        <DetailField layout="inline" label="Molduras" value={formatCurrency(bill.frames)} />
                      )}
                      {bill.establishments > 0 && (
                        <DetailField
                          layout="inline"
                          label="Estabelecimentos"
                          value={formatCurrency(bill.establishments)}
                        />
                      )}
                      {bill.added > 0 && (
                        <DetailField layout="inline" label="Adicionados" value={formatCurrency(bill.added)} />
                      )}
                      {bill.removed > 0 && (
                        <DetailField layout="inline" label="Removidos" value={formatCurrency(bill.removed)} />
                      )}
                      <Flex justify="space-between" align="baseline" pt={2}>
                        <Text fontWeight="700" color="gray.800">
                          Total a cobrar
                        </Text>
                        <Text fontWeight="700" fontSize="lg" color="gray.800">
                          {formatCurrency(bill.total)}
                        </Text>
                      </Flex>
                    </VStack>
                  </Box>
                </VStack>
              </Box>

              <Box minW={0}>
                <SectionTitle as="h3">Pagamento</SectionTitle>
                <VStack align="stretch" spacing={4}>
                  <Box>
                    <SectionTitle variant="mini" as="h4">
                      Comprador
                    </SectionTitle>
                    <VStack align="stretch" spacing={3}>
                      <FormField label="Nome" error={errors['buyer.name']}>
                        <Input value={buyer.name} onChange={(e) => setBuyer({ ...buyer, name: e.target.value })} />
                      </FormField>
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                        <FormField label="CPF" error={errors['buyer.cpf']} isRequired={hasArtworks}>
                          <MaskedInput mask="cpf" value={buyer.cpf} onChange={(cpf) => setBuyer({ ...buyer, cpf })} />
                        </FormField>
                        <FormField label="Celular" error={errors['buyer.phone']} isRequired={hasArtworks}>
                          <MaskedInput
                            mask="phone"
                            value={buyer.phone}
                            onChange={(phone) => setBuyer({ ...buyer, phone })}
                          />
                        </FormField>
                      </SimpleGrid>
                      {hasArtworks && (
                        <Text variant="caption">
                          CPF e celular vão para o recibo da obra; o que for editado atualiza o cadastro do inscrito.
                        </Text>
                      )}
                    </VStack>
                  </Box>

                  {hasCharge ? (
                    <>
                      <FormField label="Forma de pagamento" error={errors.payment_method} isRequired>
                        <FormSelect
                          value={method}
                          onChange={(value) => setMethod(value as PanelCheckoutPaymentMethod | '')}
                          options={METHOD_OPTIONS}
                        />
                      </FormField>
                      <FormField label="Observação" error={errors.payment_method_note}>
                        <Input value={note} maxLength={255} onChange={(e) => setNote(e.target.value)} />
                      </FormField>
                      <FileInput
                        accept="receipt"
                        maxSizeMB={PROOF_MAX_MB}
                        label="Comprovante"
                        isRequired={proofRequired}
                        helperText={method === 'cash' ? 'Opcional em dinheiro.' : undefined}
                        preview="thumbnail"
                        value={proof}
                        onChange={setProof}
                        error={errors.proof_image}
                      />
                      {hasArtworks && (
                        <Alert status="info" variant="subtle" borderRadius="md">
                          <AlertIcon />
                          <AlertDescription fontSize="sm">
                            O comprador recebe o e-mail de arremate do leilão ao confirmar.
                          </AlertDescription>
                        </Alert>
                      )}
                    </>
                  ) : (
                    <Alert status="info" variant="subtle" borderRadius="md">
                      <AlertIcon />
                      <AlertDescription fontSize="sm">
                        Nada a cobrar: a comanda será fechada sem pagamento.
                      </AlertDescription>
                    </Alert>
                  )}
                </VStack>
              </Box>
            </SimpleGrid>
          </>
        )}
      </FormModal>

      <ConfirmDialog
        isOpen={artToRemove !== null}
        onClose={() => setArtToRemove(null)}
        onConfirm={() => {
          if (artToRemove) {
            setAdjustments((prev) => ({ ...prev, removedArtIds: [...prev.removedArtIds, artToRemove.id] }))
          }
          setArtToRemove(null)
        }}
        action="delete"
        title="Remover obra da cobrança?"
        description={`"${artToRemove?.title}" sai da cobrança e fica sem comprador: depois do check-out ela pode ser vendida no pós-leilão ou devolvida ao artista.`}
        confirmLabel="Remover"
        irreversible={false}
      />
    </>
  )
}
